import React from 'react';
import './SecurityLockoutMessage.css';

const SecurityLockoutMessage = ({ error, onRetry, onForgotPassword }) => {
  if (!error || !error.type) return null;

  const formatTimeRemaining = (lockedUntil) => {
    if (!lockedUntil) return '';
    
    const now = new Date();
    const unlockTime = new Date(lockedUntil);
    const diffMs = unlockTime - now;
    
    if (diffMs <= 0) return '';
    
    const minutes = Math.ceil(diffMs / 60000);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const renderLockoutMessage = () => {
    const timeRemaining = formatTimeRemaining(error.lockedUntil);
    
    return (
      <div className="security-lockout-container">
        <div className="security-lockout-icon">
          <i className="fas fa-shield-alt"></i>
        </div>
        <div className="security-lockout-content">
          <h3>Account Temporarily Locked</h3>
          <p className="lockout-message">
            {error.message || 'Your account has been temporarily locked due to multiple failed login attempts.'}
          </p>
          {timeRemaining && (
            <p className="time-remaining">
              <strong>Time remaining:</strong> {timeRemaining}
            </p>
          )}
          {error.lockoutCount > 1 && (
            <p className="lockout-count">
              This is lockout #{error.lockoutCount}. Subsequent lockouts will be longer.
            </p>
          )}
          <div className="lockout-actions">
            <button 
              type="button" 
              className="btn btn-outline-primary"
              onClick={onForgotPassword}
            >
              Reset Password
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onRetry}
            >
              Try Again Later
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRateLimitMessage = () => (
    <div className="security-lockout-container rate-limit">
      <div className="security-lockout-icon">
        <i className="fas fa-clock"></i>
      </div>
      <div className="security-lockout-content">
        <h3>Too Many Attempts</h3>
        <p className="lockout-message">
          {error.message || 'Too many login attempts from your location. Please wait before trying again.'}
        </p>
        {error.retryAfter && (
          <p className="time-remaining">
            <strong>Please wait:</strong> {Math.ceil(error.retryAfter / 60)} minutes
          </p>
        )}
        <div className="lockout-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onRetry}
          >
            Understood
          </button>
        </div>
      </div>
    </div>
  );

  const renderFailedAttemptsWarning = () => (
    <div className="security-warning-container">
      <div className="security-warning-icon">
        <i className="fas fa-exclamation-triangle"></i>
      </div>
      <div className="security-warning-content">
        <p className="warning-message">
          <strong>Security Notice:</strong> {error.failedAttempts} failed attempt{error.failedAttempts !== 1 ? 's' : ''}.
          {error.remainingAttempts !== undefined && (
            <span> {error.remainingAttempts} attempt{error.remainingAttempts !== 1 ? 's' : ''} remaining before account lockout.</span>
          )}
        </p>
        <p className="security-tip">
          If you've forgotten your password, use the "Forgot Password" link below.
        </p>
      </div>
    </div>
  );

  switch (error.type) {
    case 'account_locked':
      return renderLockoutMessage();
    case 'ip_rate_limited':
      return renderRateLimitMessage();
    case 'failed_attempts_warning':
      return renderFailedAttemptsWarning();
    default:
      return null;
  }
};

export default SecurityLockoutMessage;
